import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import JsonLd from "@/components/feature/JsonLd";
import ArticleCard from "@/components/feature/ArticleCard";
import { usePageSeo } from "@/hooks/usePageSeo";
import { SITE } from "@/lib/site";
import { formatCount, formatDate } from "@/lib/format";
import { articles } from "@/mocks/articles";

export default function ArticlePage() {
  const { id = "" } = useParams();
  const article = articles.find((item) => String(item.id) === id);
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(article?.likes ?? 0);
  const [copied, setCopied] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    window.scrollTo({ top: 0 });
    setLiked(false);
    setLikes(article?.likes ?? 0);
  }, [id, article]);

  useEffect(() => {
    const onScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? Math.min(100, (window.scrollY / total) * 100) : 0);
    };
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [id]);

  const related = useMemo(
    () =>
      article
        ? articles
            .filter((item) => item.categorySlug === article.categorySlug && item.id !== article.id)
            .slice(0, 3)
        : [],
    [article],
  );

  const sideHot = useMemo(() => [...articles].sort((a, b) => b.views - a.views).slice(0, 5), []);

  usePageSeo({
    title: article ? article.title : "爆料未找到",
    description: article ? article.excerpt : "该爆料内容暂时无法访问，请返回首页查看最新内容。",
    keywords: article ? `${article.tags.join(",")},爆料专区,今日爆料` : SITE.keywords,
    path: `/article/${id}`,
    type: "article",
  });

  const toggleLike = () => {
    setLiked((prev) => {
      setLikes((count) => (prev ? count - 1 : count + 1));
      return !prev;
    });
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  if (!article) {
    return (
      <div className="flex min-h-screen flex-col bg-background-50">
        <SiteHeader />
        <main className="flex flex-1 flex-col items-center justify-center px-4 py-20 text-center">
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-emotion-sad-line text-3xl"></i>
          </span>
          <h1 className="mt-5 font-heading text-2xl tracking-wide text-foreground-950">这条爆料找不到了</h1>
          <p className="mt-2 text-sm text-foreground-600">它可能已经被撤下，或者链接不太对。</p>
          <Link
            to="/"
            className="mt-6 inline-flex h-11 items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
          >
            回到首页
          </Link>
        </main>
        <SiteFooter />
      </div>
    );
  }

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.excerpt,
      image: [article.cover],
      datePublished: article.date,
      dateModified: article.date,
      author: { "@type": "Organization", name: article.author },
      publisher: { "@type": "Organization", name: SITE.name },
      articleSection: article.categoryName,
      keywords: article.tags.join(","),
      url: `${SITE.domain}/article/${article.id}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "首页", item: SITE.domain },
        {
          "@type": "ListItem",
          position: 2,
          name: article.categoryName,
          item: `${SITE.domain}/category/${article.categorySlug}`,
        },
        { "@type": "ListItem", position: 3, name: article.title, item: `${SITE.domain}/article/${article.id}` },
      ],
    },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <JsonLd data={jsonLd} />
      <div className="fixed inset-x-0 top-0 z-[60] h-0.5 bg-transparent">
        <div className="h-full bg-primary-500 transition-[width] duration-150" style={{ width: `${progress}%` }}></div>
      </div>
      <SiteHeader />

      <main className="flex-1">
        <div className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-7xl px-4 py-5 md:px-8">
            <nav aria-label="面包屑" className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <Link to="/" className="transition-colors hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to={`/category/${article.categorySlug}`} className="transition-colors hover:text-primary-600">
                {article.categoryName}
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="line-clamp-1 text-foreground-800">{article.title}</span>
            </nav>
          </div>
        </div>

        <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-8 md:py-10">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_320px]">
            <article>
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-200"
              >
                <i className="ri-price-tag-3-line"></i>
                {article.categoryName}
              </Link>
              <h1 className="mt-4 font-heading text-2xl leading-tight tracking-wide text-foreground-950 md:text-4xl">
                {article.title}
              </h1>
              <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-3-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {formatDate(article.date)}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  约 {article.readTime} 分钟读完
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line"></i>
                  {formatCount(article.views)} 次围观
                </span>
              </div>

              <div className="mt-6 h-64 w-full overflow-hidden rounded-xl bg-background-200 md:h-[420px]">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={`${article.title} - ${article.categoryName}`}
                  className="h-full w-full object-cover"
                />
              </div>

              <div className="article-body mt-8 text-[15px] leading-relaxed text-foreground-800">
                {article.content.map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-secondary-100 px-3 py-1 text-xs text-secondary-900">
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
                <button
                  type="button"
                  onClick={toggleLike}
                  className={`flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md px-5 text-sm font-medium transition-colors ${
                    liked ? "bg-primary-500 text-background-50" : "bg-primary-100 text-primary-700 hover:bg-primary-200"
                  }`}
                >
                  <i className={liked ? "ri-heart-fill" : "ri-heart-line"}></i>
                  {liked ? "已点赞" : "点赞"} · {formatCount(likes)}
                </button>
                <button
                  type="button"
                  onClick={copyLink}
                  className="flex h-10 cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
                >
                  <i className={copied ? "ri-check-line" : "ri-link"}></i>
                  {copied ? "链接已复制" : "复制链接"}
                </button>
                <span className="flex items-center gap-1 text-xs text-foreground-500">
                  <i className="ri-chat-3-line"></i>
                  {formatCount(article.comments)} 条讨论
                </span>
              </div>

              <div className="mt-8 rounded-lg bg-primary-50 p-4 text-xs leading-relaxed text-foreground-600">
                本文内容整理自网络公开信息，仅用于话题讨论与阅读参考，相关情况请以权威渠道发布的信息为准。
              </div>
            </article>

            <aside className="space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="flex items-center gap-2 font-heading text-lg tracking-wide text-foreground-950">
                  <span className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-500 text-background-50">
                    <i className="ri-fire-fill text-sm"></i>
                  </span>
                  大家还在看
                </h2>
                <ol className="mt-4 space-y-3">
                  {sideHot.map((item, index) => (
                    <li key={item.id}>
                      <Link to={`/article/${item.id}`} className="group flex gap-3">
                        <span className="w-4 shrink-0 font-heading text-lg text-primary-500">{index + 1}</span>
                        <span className="min-w-0">
                          <span className="line-clamp-2 text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-600">
                            {item.title}
                          </span>
                          <span className="mt-1 block text-xs text-foreground-400">
                            {formatCount(item.views)} 次围观
                          </span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-lg tracking-wide text-foreground-950">关于本站</h2>
                <p className="mt-3 text-sm leading-relaxed text-foreground-600">
                  爆料专区每天整理今日热点、新鲜事与吃瓜合集，帮你用最少的时间跟上全网话题。
                </p>
                <Link
                  to="/about"
                  className="mt-4 inline-flex items-center gap-1 text-sm text-primary-600 transition-colors hover:text-primary-700"
                >
                  了解我们的内容原则
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </aside>
          </div>

          {related.length > 0 && (
            <section className="mt-14">
              <div className="flex items-end justify-between border-b border-background-200 pb-3">
                <h2 className="font-heading text-xl tracking-wide text-foreground-950 md:text-2xl">
                  更多{article.categoryName}
                </h2>
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="flex items-center gap-1 whitespace-nowrap text-sm text-primary-600 transition-colors hover:text-primary-700"
                >
                  查看全部
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {related.map((item) => (
                  <ArticleCard key={item.id} article={item} />
                ))}
              </div>
            </section>
          )}
        </div>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}